import classnames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import { camelCase } from "lodash";
import React, { useState, useEffect } from "react";

import CheckBox from "@krea/customer-features/shared-components/fields/checkbox";
import { Text } from "@krea/customer-features/shared-components/text";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import {
  useGetFieldRequests,
  useGetOneFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/queries";

import { FieldRequestForm } from "../GetFieldRequestForm";

const GroupedFieldRequestValuesToFieldRequestFields = {
  REAL_ESTATE_MORTGAGE: "PROPERTY",
  PERSONAL_GUARANTEE: "GUARANTORS",
  PARENT_COMPANY_GUARANTEE: "PARENT_COMPANY_GUARANTEE",
  SHARE_MORTGAGE: "SHARE_MORTGAGE",

  REFINANCING: "REFINANCING",
  PROPERTY_REVENUE: "PROPERTY_CASH_FLOW",
  COMPANY_REVENUE: "ACCOUNTING_DATA",
  CONSTRUCTION_COMPLETED: "CONSTRUCTION_COMPLETED_REPAYMENT",
};

const motionItem = {
  hidden: {
    opacity: 0,
    height: 0,
    border: "1px solid rgba(255, 255, 255, 0)",
    marginTop: "0px",
  },
  visible: {
    border: "1px solid #39008c",
    opacity: 1,
    height: "auto",
    marginTop: "24px",
  },
};

const motionSubTitle = {
  hidden: {
    opacity: 0,
    height: 0,
    marginBottom: "0px",
  },
  visible: {
    opacity: 1,
    height: "auto",
    marginBottom: "16px",
  },
};

const FieldRequestBox = ({ children, active, id, ...rest }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    active &&
      setTimeout(() => {
        setIsOpen(true);
      }, 100);
  }, [active]);

  return (
    <motion.div
      variants={motionItem}
      initial="hidden"
      animate={isOpen && "visible"}
      transition={{
        default: { duration: 0.6, ease: "easeOut" },
      }}
      exit="hidden"
      className="overflow-hidden rounded"
      {...rest}
    >
      <div className="pt-6 px-5 pb-5 position-relative">{children}</div>
    </motion.div>
  );
};

function GroupedFieldRequestForm({ fieldRequestID }) {
  const { data: allFieldRequests = [] } = useGetFieldRequests();
  const { data: { submittedData, subtitle } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest } = usePostFieldRequest(fieldRequestID);

  const [selectedChoices, setSelectedChoices] = useState([
    ...submittedData.choices,
  ]);

  const fieldRequestsToRender =
    selectedChoices.map((choice) => {
      return {
        field: camelCase(
          GroupedFieldRequestValuesToFieldRequestFields[choice.value],
        ),
        groupedFieldRequestValue: choice.value,
        request: allFieldRequests.find(
          (f) =>
            f.field ===
            camelCase(
              GroupedFieldRequestValuesToFieldRequestFields[choice.value],
            ),
        ),
      };
    }) || [];

  return (
    <>
      <AnimatePresence>
        {subtitle ? (
          <motion.div
            variants={motionSubTitle}
            initial="hidden"
            animate={subtitle && "visible"}
            transition={{
              default: { duration: 0.3, ease: "easeOut" },
            }}
            exit="hidden"
          ></motion.div>
        ) : null}
      </AnimatePresence>

      {submittedData.choices.map((choice, index) => {
        return (
          <CheckBox
            id={choice.value}
            label={choice.name}
            key={index}
            className={classnames({
              "mb-2": selectedChoices.length !== index + 1,
            })}
            onChange={(e) => {
              const updatedChoice = selectedChoices.find(
                ({ name }) => choice.name === name,
              );
              const choicesWithoutUpdatedOne = selectedChoices.filter(
                ({ name }) => choice.name !== name,
              );
              const newChoices = [
                ...choicesWithoutUpdatedOne,
                { ...updatedChoice, selected: e.target.checked },
              ];

              setSelectedChoices(newChoices);
              postFieldRequest({ choices: newChoices });
            }}
            checked={choice.selected}
          />
        );
      })}
      <div>
        <AnimatePresence>
          {submittedData.choices
            .filter(({ selected }) => selected)
            .map((choice) => {
              const { field, request } = fieldRequestsToRender.find(
                (field) => field.groupedFieldRequestValue === choice.value,
              );
              const { subtitle, id: fieldRequestID } = request;

              return (
                <FieldRequestBox key={field} active>
                  <h5 className="mb-4 pr-5">{choice.name}</h5>
                  {subtitle ? (
                    <Text size="sm" className="mb-4 font-weight-bold">
                      {subtitle}
                    </Text>
                  ) : null}
                  <FieldRequestForm
                    fieldRequestID={fieldRequestID}
                    withInputLabel={false}
                  />
                </FieldRequestBox>
              );
            })}
        </AnimatePresence>
      </div>
    </>
  );
}

export default GroupedFieldRequestForm;
